import type { AppProps } from 'next/app';
import Layout from 'components/Layout';
import { DevDashProvider } from '@/app_lib/devdash';
import AdsProvider from 'providers/AdProvider';
import { UserProvider } from 'providers/UserProvider';
import type { PageAdConfig } from 'interfaces/ads/Ad';
import type { PianoPageConfig } from 'services/Piano/entities/PianoPageConfig';
import { QuerylyScript } from 'services/Queryly';
import type { HeaderNav } from 'interfaces/navigation/Header';
import Head from 'components/Head';
import type { HeadData } from 'utils/metaDataUtils/headData';
import type { PageViewDataLayerObject } from 'services/Gtm/entities';
import { useRouter } from 'next/router';
import Loader from 'components/Loader';
import 'styles/Global.css';
import Error500 from 'components/Error500';
import type { ArcSpanPageConfig } from 'services/ArcSpan/entities';
import type { BypassPaywall } from 'interfaces/BypassPaywall';
import type { FooterProps } from 'components/Footer';
import { ArcherAdsScript } from 'services/ArcherAds';
import type { RecommendsFooterProps } from 'components/RecommendsFooter';
import { RedVentureScript } from 'services/RedVenture';
import { BinanceWidgetScript } from 'services/Binance';
import { PushlyScript } from 'services/Pushly';
import { Microsites, cryptoSectionSlugs } from 'constants/constants';
import { StyleSheetManager, ThemeProvider } from 'styled-components';
import { PersonalizationProvider } from 'providers/PersonalizationProvider';
import theme from 'styles/theme';
import GoogleTagManager from 'services/Gtm';
import NoticeProvider from 'providers/NoticeProvider';
import PaywallProvider from 'providers/PaywallProvider';
import 'styles/globalReset.css';
import type { Hub } from 'interfaces/Home';
import ErrorBoundary from 'components/ErrorBoundary';
import { UIFragments } from 'utils/log/constants/uiFragments';
import { AppErrorSeverity } from 'utils/log/constants/errorSeverity';
import thirdPartyScriptErrorObserver from 'utils/miscUtils/thirdPartyScriptErrorObserver';
import { OptimizelyProvider, OptimizelyScript } from 'providers/OptimizelyProvider';
import type { Article } from 'interfaces/content/articles/Articles';
import Error500Standalone from './500';
import useClientSideErrorReporting from 'hooks/useClientSideErrorReporting';
import { ObservedScripts } from '@/main/constants/ObservedScripts';
import { DevCookieCleanup } from '@/app_lib/scripts/devCookieCleanup';

const isProduction = process.env.ENVIRONMENT?.toLowerCase() === 'prod';

export interface PageProps {
  article?: Article;
  bypassPaywall?: BypassPaywall;
  dataLayer: PageViewDataLayerObject;
  hasArcherAds?: boolean;
  headData: HeadData;
  headerNav: HeaderNav;
  section?: Hub;
  footer?: FooterProps | RecommendsFooterProps;
  pageAdConfig: PageAdConfig;
  arcSpanPageConfig: ArcSpanPageConfig;
  pianoPageConfig: PianoPageConfig;
  redVenture?: boolean;
  widgetCss?: string | null;
  hideHeaderFooter?: boolean;
  isBranded?: boolean;
}

const App = ({ Component, pageProps }: AppProps) => {
  const router = useRouter();

  // Mount error reporting utilities for client & 3rd party scripts
  useClientSideErrorReporting();
  thirdPartyScriptErrorObserver(ObservedScripts);

  // Handle standalone pages
  const isStandalonePage = router.pathname === '/errors/500';

  if (isStandalonePage) {
    return (
      <ThemeProvider theme={theme('')}>
        <Component />
      </ThemeProvider>
    );
  }

  // Handle loadder
  if (router.isFallback) {
    return <Loader />;
  }

  // Standard pages follow
  const {
    bypassPaywall,
    dataLayer,
    hideHeaderFooter,
    hasArcherAds,
    isBranded,
    headData: {
      authors,
      databaseId,
      dateGmt,
      description,
      faqSchema,
      hasStnVideo,
      hasVanityUrl,
      image,
      imageTwitter,
      jsonLdSchema,
      keywords,
      link,
      rssFeedLink,
      socialDescription,
      socialTitle,
      title,
      pageType,
      premiumCategory,
      primarySection,
      siteName,
      canonicalUrl,
      tagNames,
      widgetCss,
      prefixedDatabaseId,
      noIndex,
    },
    section,
    footer,
    headerNav,
    pageAdConfig,
    arcSpanPageConfig,
    pianoPageConfig,
    redVenture,
    ...rest
  } = pageProps as PageProps;
  const evergreenTags = ['Personal Finance Evergreen', 'Health Evergreen'];
  const hasEvergreenTag = tagNames?.some((tag) => evergreenTags.includes(tag));
  const hasLeaderboardSlot = pageAdConfig?.slots.some((slot) => slot.placementName === 'Leaderboard') ?? false;
  const hideLeaderboard =
    pageProps?.brandedArticle?.adTags?.some((tag) => tag?.name === 'hide-leaderboard-ad') || false;
  const leaderboard = !hasEvergreenTag && hasLeaderboardSlot && !hideLeaderboard;

  const useBinanceWidget =
    Microsites.Crypto === headerNav?.subDomain ||
    pageProps?.section?.name === 'Crypto' ||
    cryptoSectionSlugs.includes(pageAdConfig?.ch);
  const usePushly = process.env.FEATURE_FLAG_PUSHLY;

  return (
    <ErrorBoundary
      fragment={UIFragments.MAIN_LAYOUT}
      severity={AppErrorSeverity.CRITICAL}
      fallback={
        <ThemeProvider theme={theme('')}>
          <Error500Standalone />
        </ThemeProvider>
      }
    >
      <UserProvider {...{ bypassPaywall, databaseId, pianoPageConfig, title }}>
        <Head
          {...{
            authors,
            canonicalUrl,
            databaseId,
            dateGmt,
            description,
            faqSchema,
            hasStnVideo,
            hasVanityUrl,
            isBranded,
            image,
            imageTwitter,
            jsonLdSchema,
            keywords,
            link,
            noIndex,
            pageAdConfig,
            pageType,
            pianoPageConfig,
            prefixedDatabaseId,
            premiumCategory,
            primarySection,
            redVenture,
            rssFeedLink,
            siteName,
            socialDescription,
            socialTitle,
            subDomain: headerNav?.subDomain,
            tagNames,
            templateType: (pageProps as PageProps).article?.templateType,
            title,
            useBinanceWidget,
            widgetCss,
          }}
        />

        <ThemeProvider theme={theme(headerNav?.subDomain)}>
          <PersonalizationProvider>
            <GoogleTagManager {...{ dataLayer }}>
              <PaywallProvider {...{ pianoPageConfig }}>
                <OptimizelyProvider pageAdConfig={pageAdConfig}>
                  <AdsProvider {...{ arcSpanPageConfig, pageAdConfig }}>
                    <StyleSheetManager enableVendorPrefixes>
                      <NoticeProvider>
                        <Layout
                          {...{
                            currentAuthors: authors,
                            currentSection: primarySection,
                            currentTags: tagNames,
                            footer,
                            hasVanityUrl,
                            headerNav,
                            hideHeaderFooter,
                            leaderboard,
                            pageAdConfig,
                            pageType,
                            section,
                          }}
                        >
                          <ErrorBoundary
                            fragment={UIFragments.PAGE_CONTENT}
                            severity={AppErrorSeverity.CRITICAL}
                            fallback={<Error500 />}
                          >
                            <Component
                              section={section}
                              {...rest}
                            />
                          </ErrorBoundary>
                        </Layout>
                      </NoticeProvider>
                    </StyleSheetManager>
                  </AdsProvider>
                  <DevDashProvider />
                </OptimizelyProvider>
              </PaywallProvider>
              <OptimizelyScript />
              <QuerylyScript />
              {hasArcherAds && <ArcherAdsScript />}
              {redVenture && <RedVentureScript />}
              {useBinanceWidget && <BinanceWidgetScript />}
              {usePushly && <PushlyScript />}
              {!isProduction && <DevCookieCleanup />}
            </GoogleTagManager>
          </PersonalizationProvider>
        </ThemeProvider>
      </UserProvider>
    </ErrorBoundary>
  );
};

export default App;
